import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useCustomizationStore } from '../store/customizationStore';
import { useLocalizationStore } from '../store/localizationStore';
import { formatCurrency } from '../utils/units';
import { 
  Star, 
  Award, 
  Shield, 
  Clock, 
  MessageCircle, 
  ArrowRight,
  Sparkles,
  Truck,
  Wrench,
  Heart,
  Gem,
  Zap,
  BadgeCheck,
  Users,
  Palette,
  Ruler,
  Table,
  Bath,
  Layout,
  Coffee,
  Sofa,
  Calendar,
  Phone,
  Mail,
  Video,
  ChevronLeft,
  ChevronRight,
  Quote,
  Check
} from 'lucide-react';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  },
  exit: { opacity: 0 }
};

const itemVariants = {
  hidden: { 
    opacity: 0,
    y: 20,
    scale: 0.95
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 30
    }
  }
};

const categories = [
  {
    id: 'dining-tables',
    name: 'Dining Tables',
    description: 'Custom tables for elegant dining spaces',
    icon: Table,
    image: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800',
    features: ['Multiple sizes', 'Premium finishes', 'Custom edges'],
    price: 1499
  },
  {
    id: 'kitchen-surfaces',
    name: 'Kitchen Surfaces',
    description: 'Premium countertops and islands',
    icon: Layout,
    image: 'https://images.unsplash.com/photo-1600607687644-c7171b42498b?w=800',
    features: ['Heat resistant', 'Seamless design', 'Built-in features'],
    price: 999
  },
  {
    id: 'bathroom-collection',
    name: 'Bathroom Collection',
    description: 'Luxury vanities and washbasins',
    icon: Bath,
    image: 'https://images.unsplash.com/photo-1600566752355-35792bedcfea?w=800',
    features: ['Integrated sinks', 'Anti-bacterial', 'Moisture resistant'],
    price: 799
  },
  {
    id: 'coffee-tables',
    name: 'Coffee Tables',
    description: 'Modern coffee and side tables',
    icon: Coffee,
    image: 'https://images.unsplash.com/photo-1600607687668-0b7b08d9d1a5?w=800',
    features: ['Contemporary designs', 'Multiple shapes', 'Custom sizes'],
    price: 699
  }
];

const benefits = [
  {
    icon: Users,
    title: '10,000+ Happy Customers',
    description: 'Join thousands of satisfied homeowners'
  },
  {
    icon: Sparkles,
    title: 'Expert Installation',
    description: 'Professional craftsmanship guaranteed'
  },
  {
    icon: Clock,
    title: 'Quick Turnaround',
    description: 'Most projects completed in 2-3 weeks'
  },
  {
    icon: BadgeCheck,
    title: 'Quality Assured',
    description: '10-year warranty on all installations'
  }
];

const testimonials = [
  {
    id: 1,
    name: 'Sarah Johnson',
    role: 'Interior Designer',
    location: 'New York, NY',
    image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=150',
    quote: 'The quality and customization options are exceptional. My clients are always impressed with the final result.',
    rating: 5,
    project: {
      type: 'Kitchen Renovation',
      image: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800',
      details: 'Custom marble countertop with integrated sink',
      completion: '2024',
      value: '$45,000'
    }
  },
  {
    id: 2,
    name: 'Michael Chen',
    role: 'Restaurant Owner',
    location: 'San Francisco, CA',
    image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150',
    quote: 'The durability and elegance of their surfaces have transformed our dining space. Excellent service from start to finish.',
    rating: 5,
    project: {
      type: 'Commercial Dining',
      image: 'https://images.unsplash.com/photo-1600607687644-c7171b42498b?w=800',
      details: 'Custom dining tables for 200-seat restaurant',
      completion: '2023',
      value: '$120,000'
    }
  },
  {
    id: 3,
    name: 'Emily Rodriguez',
    role: 'Luxury Home Developer',
    location: 'Miami, FL',
    image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150',
    quote: 'Their attention to detail and premium materials have become a cornerstone of our luxury home offerings.',
    rating: 5,
    project: {
      type: 'Penthouse Suite',
      image: 'https://images.unsplash.com/photo-1600566752355-35792bedcfea?w=800',
      details: 'Complete surface package for luxury penthouse',
      completion: '2024',
      value: '$280,000'
    }
  }
];

export const MainPage: React.FC = () => {
  const { setStep } = useCustomizationStore();
  const { currency, language } = useLocalizationStore();
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [showContactForm, setShowContactForm] = useState(false);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTestimonial(prev => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const handleCategorySelect = (categoryId: string) => {
    setActiveCategory(categoryId);
    setTimeout(() => {
      setStep('material-selection');
    }, 300);
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      className="min-h-screen bg-cream"
    >
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-r from-black/50 to-transparent z-10" />
          <img
            src="https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=1920"
            alt="Luxury surface"
            className="w-full h-full object-cover"
          />
        </div>

        <div className="relative z-20 max-w-6xl mx-auto px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="max-w-3xl"
          >
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-6 py-3 rounded-full mb-8">
              <Award className="w-5 h-5" />
              <span>Europe's Most Trusted Surface Provider</span>
            </div>
            
            <h1 className="text-6xl font-bold mb-6 text-white">
              Transform Your Space with Premium Porcelain Surfaces
            </h1>
            
            <p className="text-xl text-white/90 mb-12">
              Experience unmatched quality and craftsmanship with our exclusive collection of European porcelain surfaces.
            </p>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setStep('material-selection')}
                className="bg-gold text-white px-8 py-4 rounded-xl font-semibold text-lg hover:bg-gold-dark transition-colors flex items-center justify-center gap-2"
              >
                Start Designing
                <ArrowRight className="w-5 h-5" />
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowContactForm(true)}
                className="bg-white/10 backdrop-blur-sm text-white border border-white/20 px-8 py-4 rounded-xl font-semibold text-lg hover:bg-white/20 transition-colors flex items-center justify-center gap-2"
              >
                Book Consultation
                <Video className="w-5 h-5" />
              </motion.button>
            </div>
          </motion.div>
        </div>

        <div className="absolute bottom-12 left-1/2 -translate-x-1/2 z-20">
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="text-white/80 text-center"
          >
            <p className="text-sm mb-2">Scroll to explore</p>
            <div className="w-6 h-10 border-2 border-white/30 rounded-full mx-auto">
              <div className="w-1.5 h-1.5 bg-white rounded-full mx-auto mt-2 animate-bounce" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-24 bg-white">
        <div className="max-w-6xl mx-auto px-8">
          <div className="text-center mb-16">
            <motion.h2
              variants={itemVariants}
              className="text-4xl font-bold text-gold-dark mb-4"
            >
              Discover Our Collections
            </motion.h2>
            <motion.p
              variants={itemVariants}
              className="text-xl text-black/70"
            >
              Choose from our range of premium surface solutions
            </motion.p>
          </div>

          <motion.div
            variants={containerVariants}
            className="grid grid-cols-1 md:grid-cols-2 gap-8"
          >
            {categories.map((category) => {
              const Icon = category.icon;
              return (
                <motion.div
                  key={category.id}
                  variants={itemVariants}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handleCategorySelect(category.id)}
                  className="bg-cream rounded-2xl overflow-hidden cursor-pointer group"
                >
                  <div className="relative h-64">
                    <img
                      src={category.image}
                      alt={category.name}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                    <div className="absolute bottom-6 left-6 text-white">
                      <h3 className="text-2xl font-semibold mb-2">{category.name}</h3>
                      <p className="text-white/80">{category.description}</p>
                    </div>
                  </div>

                  <div className="p-6">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="p-3 bg-gold/10 rounded-xl">
                        <Icon className="w-6 h-6 text-gold" />
                      </div>
                      <div>
                        <p className="text-sm text-black/60">Starting from</p>
                        <p className="text-xl font-bold text-gold">
                          {formatCurrency(category.price, currency, language)}
                        </p>
                      </div>
                    </div>

                    <ul className="space-y-2">
                      {category.features.map((feature) => (
                        <li key={feature} className="flex items-center gap-2 text-sm text-black/70">
                          <Check className="w-4 h-4 text-gold" />
                          {feature}
                        </li>
                      ))}
                    </ul>

                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="w-full mt-6 bg-gold text-white py-3 rounded-xl font-medium hover:bg-gold-dark transition-colors flex items-center justify-center gap-2"
                    >
                      Customize Now
                      <ArrowRight className="w-5 h-5" />
                    </motion.button>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 bg-gradient-to-br from-gold/5 to-gold/10">
        <div className="max-w-6xl mx-auto px-8">
          <div className="text-center mb-16">
            <motion.h2
              variants={itemVariants}
              className="text-4xl font-bold text-gold-dark mb-4"
            >
              Why Choose Us
            </motion.h2>
            <motion.p
              variants={itemVariants}
              className="text-xl text-black/70"
            >
              Experience excellence in every detail
            </motion.p>
          </div>

          <motion.div
            variants={containerVariants}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {benefits.map((benefit) => {
              const Icon = benefit.icon;
              return (
                <motion.div
                  key={benefit.title}
                  variants={itemVariants}
                  className="bg-white rounded-xl p-6 shadow-lg"
                >
                  <div className="p-3 bg-gold/10 rounded-xl inline-block mb-4">
                    <Icon className="w-6 h-6 text-gold" />
                  </div>
                  <h3 className="text-xl font-semibold text-gold-dark mb-2">
                    {benefit.title}
                  </h3>
                  <p className="text-black/70">
                    {benefit.description}
                  </p>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Enhanced Premium Testimonials Section */}
      <section className="py-32 bg-[#1C1C1C] relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(255,255,255,0.15) 2px, transparent 0)',
            backgroundSize: '40px 40px'
          }} />
        </div>
        
        {/* Gradient Overlays */}
        <div className="absolute top-0 left-0 w-1/3 h-1/2 bg-gradient-to-br from-gold/20 to-transparent opacity-30 blur-3xl" />
        <div className="absolute bottom-0 right-0 w-1/2 h-1/3 bg-gradient-to-tl from-gold/10 to-transparent opacity-20 blur-3xl" />

        <div className="max-w-7xl mx-auto px-8 relative">
          <div className="text-center mb-20">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="inline-flex items-center gap-2 bg-gold/10 text-gold px-6 py-3 rounded-full mb-8"
            >
              <Star className="w-5 h-5" />
              <span className="font-medium">Client Success Stories</span>
            </motion.div>
            
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-5xl font-bold text-white mb-6"
            >
              Transforming Visions into Reality
            </motion.h2>
            
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-xl text-white/70 max-w-3xl mx-auto"
            >
              See how our premium surfaces have elevated spaces across luxury homes, high-end restaurants, and exclusive commercial projects.
            </motion.p>
          </div>

          <div className="relative">
            <AnimatePresence mode="wait">
              {testimonials.map((testimonial, index) => (
                <motion.div
                  key={testimonial.id}
                  initial={{ opacity: 0, x: 100 }}
                  animate={index === currentTestimonial ? { opacity: 1, x: 0 } : { opacity: 0, x: -100 }}
                  exit={{ opacity: 0, x: -100 }}
                  transition={{ type: "spring", stiffness: 300, damping: 30 }}
                  className={`${index === currentTestimonial ? 'block' : 'hidden'}`}
                >
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                    {/* Project Image Side */}
                    <div className="relative group">
                      <div className="absolute inset-0 bg-gradient-to-r from-gold/20 to-gold/5 group-hover:opacity-75 transition-opacity duration-500" />
                      <img
                        src={testimonial.project.image}
                        alt={testimonial.project.type}
                        className="w-full h-[600px] object-cover rounded-2xl"
                      />
                      <div className="absolute bottom-6 left-6 right-6 bg-black/80 backdrop-blur-sm p-6 rounded-xl">
                        <h4 className="text-white font-semibold mb-2">{testimonial.project.type}</h4>
                        <p className="text-white/80 text-sm">{testimonial.project.details}</p>
                        <div className="flex items-center gap-6 mt-4 text-sm text-white/60">
                          <span>Completed: {testimonial.project.completion}</span>
                          <span>Value: {testimonial.project.value}</span>
                        </div>
                      </div>
                    </div>

                    {/* Testimonial Content Side */}
                    <div className="relative">
                      <Quote className="absolute -top-6 -left-6 w-12 h-12 text-gold/20" />
                      <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
                        <div className="flex items-center gap-4 mb-8">
                          <img
                            src={testimonial.image}
                            alt={testimonial.name}
                            className="w-16 h-16 rounded-full object-cover ring-4 ring-gold/20"
                          />
                          <div>
                            <h4 className="text-white font-semibold text-xl">{testimonial.name}</h4>
                            <p className="text-gold">{testimonial.role}</p>
                            <p className="text-white/60 text-sm">{testimonial.location}</p>
                          </div>
                        </div>

                        <blockquote className="text-white/90 text-xl font-light italic mb-8 leading-relaxed">
                          "{testimonial.quote}"
                        </blockquote>

                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-1">
                            {[...Array(testimonial.rating)].map((_, i) => (
                              <Star
                                key={i}
                                className="w-5 h-5 text-gold fill-current"
                              />
                            ))}
                          </div>
                          <div className="flex items-center gap-2 text-white/60">
                            <BadgeCheck className="w-5 h-5 text-gold" />
                            <span>Verified Project</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>

            {/* Navigation Controls */}
            <div className="flex justify-center items-center gap-8 mt-12">
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length)}
                className="p-4 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-white transition-colors"
              >
                <ChevronLeft className="w-6 h-6" />
              </motion.button>

              <div className="flex items-center gap-3">
                {testimonials.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentTestimonial(index)}
                    className={`w-3 h-3 rounded-full transition-all duration-300 ${
                      index === currentTestimonial
                        ? 'bg-gold w-12'
                        : 'bg-white/20 hover:bg-white/40'
                    }`}
                  />
                ))}
              </div>

              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => setCurrentTestimonial((prev) => (prev + 1) % testimonials.length)}
                className="p-4 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-white transition-colors"
              >
                <ChevronRight className="w-6 h-6" />
              </motion.button>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-24 bg-gradient-to-br from-gold to-gold-dark text-white">
        <div className="max-w-6xl mx-auto px-8">
          <div className="text-center mb-16">
            <motion.h2
              variants={itemVariants}
              className="text-4xl font-bold mb-4"
            >
              Ready to Transform Your Space?
            </motion.h2>
            <motion.p
              variants={itemVariants}
              className="text-xl text-white/90"
            >
              Our design experts are here to help bring your vision to life
            </motion.p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.a
              variants={itemVariants}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              href="tel:+18001234567"
              className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center hover:bg-white/20 transition-colors"
            >
              <Phone className="w-8 h-8 mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">Call Us</h3>
              <p className="text-white/80">+1 (800) 123-4567</p>
            </motion.a>

            <motion.a
              variants={itemVariants}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              href="mailto:design@example.com"
              className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center hover:bg-white/20 transition-colors"
            >
              <Mail className="w-8 h-8 mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">Email Us</h3>
              <p className="text-white/80">design@example.com</p>
            </motion.a>

            <motion.button
              variants={itemVariants}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowContactForm(true)}
              className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center hover:bg-white/20 transition-colors"
            >
              <Calendar className="w-8 h-8 mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">Book Consultation</h3>
              <p className="text-white/80">Schedule a free design session</p>
            </motion.button>
          </div>
        </div>
      </section>
    </motion.div>
  );
};